import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Link, Text } from '@bonusly/design-system';

import { useBizyLinkClick } from '../context/link-context';
import { ChatAlt2Outline, DocumentTextOutline, SparklesOutline } from '../lib/icons';
import { BizyRecognitionInline } from '../lib/recognition-inline';
import type { BizyNodeProps } from './node-props';

type ContentCardType = 'recognition' | 'meeting' | 'generic';

interface MetadataItem {
	key: string;
	value: string;
}

type ContentCardPayload = {
	variant?: string;
	contentType?: ContentCardType;
	title?: string;
	giverName?: string;
	recipients?: string[];
	amount?: number;
	message?: string;
	hashtags?: string[];
	date?: string;
	content?: string;
	href?: string;
	metadata?: MetadataItem[];
};

const TYPE_CONFIG: Record<ContentCardType, { labelKey: string; icon: ReactNode; color: string }> = {
	recognition: { labelKey: 'content_card.label.recognition', icon: <SparklesOutline boxSize={3.5} />, color: 'primary.fg' },
	meeting: { labelKey: 'content_card.label.meeting', icon: <ChatAlt2Outline boxSize={3.5} />, color: 'info.icon' },
	generic: { labelKey: 'content_card.label.generic', icon: <DocumentTextOutline boxSize={3.5} />, color: 'text.softer' },
};

/**
 * Legacy `content_card` node — a read-only snapshot of a recognition, meeting,
 * or generic content item. The recognition variant renders an inline feed-style
 * preview; everything else renders the labelled content card.
 */
export function ContentCardNode({ node }: BizyNodeProps) {
	const { t } = useTranslation('admin_bizy');
	const onLinkClick = useBizyLinkClick();
	const p = (node as { payload: ContentCardPayload }).payload;

	if (p.variant === 'recognition') {
		return (
			<Box my={2}>
				<Box border="1px solid" borderColor="border.soft" borderRadius={12} bg="bg.surface" overflow="hidden" px={3} py={3}>
					<Text fontSize="sm" color="text.default" lineHeight="tall">
						{p.giverName && (
							<Text as="span" fontSize="inherit" fontWeight="semibold">
								{p.giverName}:{' '}
							</Text>
						)}
						<BizyRecognitionInline recipients={p.recipients} amount={p.amount} message={p.message} hashtags={p.hashtags} />
					</Text>
					<Flex align="center" justify="space-between" mt={2}>
						<Text fontSize="xs" color="text.softest">
							{p.date}
						</Text>
						{p.href && (
							<Link
								href={p.href}
								fontSize="xs"
								color="text.interactive"
								onClick={(e: React.MouseEvent) => {
									e.preventDefault();
									if (p.href) onLinkClick(p.href);
								}}
							>
								{t('approval_card.view')}
							</Link>
						)}
					</Flex>
				</Box>
			</Box>
		);
	}

	const type = p.contentType ?? 'generic';
	const config = TYPE_CONFIG[type];

	return (
		<Box my={2}>
			<motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15, ease: 'easeOut' }}>
				<Box border="1px solid" borderColor="border.soft" borderRadius={12} bg="bg.surface" overflow="hidden" px={3} py={2.5}>
					<Flex direction="column" gap={2}>
						<Flex align="center" gap={1.5}>
							<Flex color={config.color} flexShrink={0}>
								{config.icon}
							</Flex>
							<Text fontSize="xs" fontWeight="medium" color={config.color} textTransform="uppercase" letterSpacing="wider">
								{t(config.labelKey)}
							</Text>
						</Flex>

						{p.title && (
							<Text fontSize="sm" fontWeight="semibold" color="text.default">
								{p.title}
							</Text>
						)}

						<Text fontSize="sm" color="text.soft" lineHeight="tall" noOfLines={4}>
							{p.content ?? ''}
						</Text>

						{p.metadata && p.metadata.length > 0 && (
							<Flex gap={3} flexWrap="wrap">
								{p.metadata.map((item) => (
									<Flex key={item.key} gap={1} align="baseline">
										<Text fontSize="xs" color="text.softer">
											{item.key}
										</Text>
										<Text fontSize="xs" color="text.default" fontWeight="medium">
											{item.value}
										</Text>
									</Flex>
								))}
							</Flex>
						)}

						{p.href && (
							<Link
								href={p.href}
								color="text.interactive"
								fontSize="xs"
								onClick={(e: React.MouseEvent) => {
									e.preventDefault();
									if (p.href) onLinkClick(p.href);
								}}
							>
								{t('approval_card.view')}
							</Link>
						)}
					</Flex>
				</Box>
			</motion.div>
		</Box>
	);
}
